import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useAppDispatch } from 'store/hooks'
import useTicketTranslations from 'utils/hooks/useTicketTranslations'
import { setAccommodationPromotionApplicable } from 'store/accommodation-slice'
import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import Text from 'components/shared/Text'
import Info from 'components/shared/Info'
import { GenericTechnicalError } from 'components/global/GenericTechnicalError'
import { CityBreakFooter } from './sections/CityBreakFooter'
import { CityBreakDetails } from './sections/CityBreakDetails'
import { CityBreakTicketsOffer } from './sections/CityBreakTicketsOffer'
import { CityBreakTicketsInCart } from './sections/CityBreakTicketsInCart'
import { CityBreakTicketsInState } from './sections/CityBreakTicketsInState'
import { CityBreakRegularTickets } from './sections/CityBreakRegularTickets'
import { useCityCart } from 'components/common/CityBreak/utils/useCityCart'
import useCityBreak from 'components/common/CityBreak/utils/useCityBreak'
import {
    CityBreakContainer,
    CityBreakInfoWrapper,
    CityBreakContentWrapper,
    PalmLeafsLeft,
    PalmLeafsLeftSm,
    PalmLeafsRight,
    PalmLeafsRightSm,
    TicketSeparator,
    TicketSeparatorSm,
} from './CityBreakBox.styles'

import palmLeafsLeft from 'assets/images/decors/promotions/cityBreak/palmLeafsLeft.svg'
import palmLeafsLeftSmall from 'assets/images/decors/promotions/cityBreak/palmLeafsLeftSmall.svg'
import palmLeafsRight from 'assets/images/decors/promotions/cityBreak/palmLeafsRight.svg'
import palmLeafsRightSmall from 'assets/images/decors/promotions/cityBreak/palmLeafsRightSmall.svg'
import ticketSeparator from 'assets/images/decors/promotions/cityBreak/ticketSeparator.svg'
import ticketSeparatorSmall from 'assets/images/decors/promotions/cityBreak/ticketSeparatorSmall.svg'

export const CityBreakBox = () => {
    const dispatch = useAppDispatch()
    const { isTranslationsError, refetch } = useTicketTranslations()
    const { cartItems } = useCityCart()
    const {
        isCityBreakApplicableForAccommo,
        isCityBreakDealingWithParkTickets,
        isCityBreakDealingWithLimitedUpsells,
    } = useCityBreak()

    const [isSelectorOpen, setIsSelectorOpen] = useState(cartItems.length > 0)
    const [isPromotionInCart, setIsPromotionInCart] = useState(
        cartItems.length > 0
    )

    const handleSubmitSuccess = () => {
        if (isCityBreakApplicableForAccommo) {
            dispatch(setAccommodationPromotionApplicable(true))
            setIsPromotionInCart(true)
        }
    }

    useEffect(() => {
        if (isPromotionInCart) {
            !isSelectorOpen && setIsSelectorOpen(true)
        }
    }, [isPromotionInCart])

    useEffect(() => {
        setIsPromotionInCart(cartItems.length > 0)
    }, [cartItems.length])

    useEffect(() => {
        if (isSelectorOpen && isTranslationsError) refetch()
    }, [isSelectorOpen])

    useEffect(() => {
        if (isCityBreakApplicableForAccommo)
            dispatch(setAccommodationPromotionApplicable(false))
    }, [])

    return (
        <CityBreakContainer>
            {/* top 'banner' view */}
            <CityBreakInfoWrapper
                isSectionOpened={isSelectorOpen}
                isFullWidth
                direction={['column', 'row']}
                gap={[isSelectorOpen ? 20 : 4, 5]}
            >
                <PalmLeafsLeft
                    src={palmLeafsLeft}
                    alt="Decorative palm leaves on the left side"
                />
                <PalmLeafsRight
                    src={palmLeafsRight}
                    alt="Decorative palm leaves on the right side"
                />
                <PalmLeafsLeftSm
                    src={palmLeafsLeftSmall}
                    alt="Small decorative palm leaves on the left side"
                />
                <PalmLeafsRightSm
                    src={palmLeafsRightSmall}
                    alt="Small decorative palm leaves on the right side"
                />
                <Box gap={8} px={[24, 0]} pt={[24, 0]} isFullWidth>
                    <Box
                        bgColor="blue_100"
                        borderColor="blue_200"
                        borderWidth={1}
                        borderRadius={4}
                        py={4}
                        px={8}
                    >
                        <Text variant="bodySmall" color="blue_600">
                            <FormattedMessage
                                id="promo.city_break.banner_badge"
                                defaultMessage="_"
                            />
                        </Text>
                    </Box>
                    <Text variant="heading3" color="secondary_500">
                        <FormattedMessage
                            id="promo.city_break.banner_title"
                            defaultMessage="_"
                        />
                    </Text>
                    <Text variant="body" color="grayscale_800">
                        <FormattedMessage
                            id="promo.city_break.banner_description"
                            defaultMessage="_"
                        />
                    </Text>
                </Box>
                <TicketSeparatorSm
                    src={ticketSeparatorSmall}
                    alt="Decorative banner separator"
                    width="100%"
                />
                {!isSelectorOpen && (
                    <Box width={['100%', '240px']} pb={[24, 0]} px={[24, 0]}>
                        <Button
                            variant="secondary"
                            width="100%"
                            hasNoWrap
                            onClick={() => {
                                setIsSelectorOpen(true)
                            }}
                            data-testid="city-button-use"
                        >
                            <FormattedMessage
                                id="promo.city_break.banner_cta"
                                defaultMessage="_"
                            />
                        </Button>
                    </Box>
                )}
            </CityBreakInfoWrapper>

            {/* unfolded content */}
            {isSelectorOpen && (
                <>
                    <TicketSeparator
                        src={ticketSeparator}
                        alt="Decorative banner separator"
                        width="100%"
                    />
                    <CityBreakContentWrapper
                        pt={[0, 14]}
                        px={[24, 80]}
                        pb={[24, 40]}
                        isFullWidth
                    >
                        {!isPromotionInCart ? (
                            <Box mt={[24, 0]}>
                                {/* detailed info */}
                                <CityBreakDetails />

                                {/* no refunds info */}
                                {isCityBreakDealingWithParkTickets &&
                                    !isTranslationsError && (
                                        <Box mt={24} isFullWidth>
                                            <Info
                                                color="info"
                                                align={['center', 'flex-start']}
                                            >
                                                <FormattedMessage
                                                    id="promo.city_break.no_restoring_tickets_msg"
                                                    defaultMessage="_"
                                                />
                                            </Info>
                                        </Box>
                                    )}

                                {isCityBreakDealingWithLimitedUpsells && (
                                    <Box mt={24} isFullWidth>
                                        <Info
                                            color="info"
                                            align={['center', 'flex-start']}
                                        >
                                            <FormattedMessage
                                                id="promo.city_break.upsells_in_cart_msg"
                                                defaultMessage="_"
                                            />
                                        </Info>
                                    </Box>
                                )}

                                {isTranslationsError ? (
                                    <Box mt={24} isFullWidth>
                                        <GenericTechnicalError />
                                    </Box>
                                ) : (
                                    <>
                                        {/* standard park tickets in cart */}
                                        {isCityBreakDealingWithParkTickets && (
                                            <CityBreakRegularTickets />
                                        )}

                                        {/* promotional tickets - offer */}
                                        <CityBreakTicketsOffer />
                                    </>
                                )}

                                {/* footer */}
                                <CityBreakFooter
                                    hasBlockingError={isTranslationsError}
                                    onCancel={() => setIsSelectorOpen(false)}
                                    onSubmitSuccess={handleSubmitSuccess}
                                />
                            </Box>
                        ) : (
                            <Box mt={[16, 0]} isFullWidth>
                                {/* promotional tickets - in cart (or waiting in state) */}
                                {isCityBreakApplicableForAccommo ? (
                                    <CityBreakTicketsInState
                                        onCtaSuccess={() => {
                                            dispatch(
                                                setAccommodationPromotionApplicable(
                                                    false
                                                )
                                            )
                                            setIsSelectorOpen(false)
                                            setIsPromotionInCart(false)
                                        }}
                                    />
                                ) : (
                                    <CityBreakTicketsInCart
                                        onCtaSuccess={() => {
                                            setIsSelectorOpen(false)
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </CityBreakContentWrapper>
                </>
            )}
        </CityBreakContainer>
    )
}
